import { z } from 'zod';
import { PANDA_PANIC_MAX_PLAYERS, PANDA_PANIC_MIN_PLAYERS, PANDA_PANIC_SLUG } from './constants.js';
import { RoomCodeSchema } from './jwt.js';

/**
 * Résultat d'une partie POSTé par le game-server à l'endpoint interne de l'API
 * NestJS en fin de partie (spec 15 §6, ADR 0022 §4). Le game-server n'accède
 * JAMAIS à la base : NestJS écrit dans Match/MatchPlayer (spec 00) après avoir
 * validé ce payload et l'appelant (secret serveur-à-serveur, câblé au lot 4c).
 */

/** Issue d'un joueur — `result` reprend `MatchResult` du socle (games.ts). */
export const PandaPanicPlayerResultSchema = z.object({
  /** Siège 0..maxPlayers-1, stable pour toute la partie. */
  index: z
    .number()
    .int()
    .min(0)
    .max(PANDA_PANIC_MAX_PLAYERS - 1),
  /** Identité (sub du JWT) : userId ou id d'invité ; `null` pour un bot. */
  sub: z.string().min(1).max(64).nullable(),
  displayName: z.string().min(1).max(40),
  isBot: z.boolean(),
  /**
   * Vrai si `sub` est un id d'INVITÉ (claim `guest` du JWT), faux si c'est un
   * userId de compte. Permet à l'API de n'écrire un `MatchPlayer` (historique
   * profil) QUE pour les comptes — les invités et bots n'en ont pas (lot 4c).
   */
  guest: z.boolean(),
  result: z.enum(['win', 'loss', 'draw', 'abandon']),
  /** Classement final (1 = vainqueur), pour l'historique du profil. */
  placement: z.number().int().min(1).max(PANDA_PANIC_MAX_PLAYERS),
});
export type PandaPanicPlayerResult = z.infer<typeof PandaPanicPlayerResultSchema>;

export const PandaPanicMatchResultSchema = z.object({
  game: z.literal(PANDA_PANIC_SLUG),
  /** Code de la salle (traçabilité). */
  room: RoomCodeSchema,
  /** Seed du RNG (rejouabilité/audit — même seed + entrées ⇒ même partie). */
  seed: z.number().int(),
  /** Durée effective de la partie, en secondes de simulation. */
  durationSeconds: z.number().finite().nonnegative(),
  isTie: z.boolean(),
  /** Un indice = vainqueur unique ; plusieurs = égalité entre eux. */
  winners: z
    .array(
      z
        .number()
        .int()
        .min(0)
        .max(PANDA_PANIC_MAX_PLAYERS - 1),
    )
    .min(1),
  players: z
    .array(PandaPanicPlayerResultSchema)
    .min(PANDA_PANIC_MIN_PLAYERS)
    .max(PANDA_PANIC_MAX_PLAYERS),
});
export type PandaPanicMatchResult = z.infer<typeof PandaPanicMatchResultSchema>;
